import React from 'react';

/**
 * AlertsActive - Displays currently active alerts
 *
 * Props:
 *   - alerts: Array of active alert objects
 *   - devices: Array of monitored devices
 *   - isLoading: boolean
 */
const AlertsActive = ({ alerts, devices, isLoading }) => {

  // Helper to format duration
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'Unknown';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  // Helper to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('fi-FI', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  // Helper to get severity styling
  const getSeverityClass = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'severity-critical';
      case 'warning':
      default:
        return 'severity-warning';
    }
  };

  // Helper to get alert icon
  const getAlertIcon = (alertType) => {
    switch (alertType?.toLowerCase()) {
      case 'offline':
        return '!'; // Could use an icon component here
      case 'network_outage':
        return '!';
      default:
        return '!';
    }
  };

  // Helper to get device name from device_id
  const getDeviceName = (deviceId) => {
    const device = devices.find(d =>
      (d.device_id === deviceId) || (d.id === deviceId)
    );
    return device?.name || deviceId;
  };

  // Helper to format alert type for display
  const formatAlertType = (alertType) => {
    if (!alertType) return 'Unknown';
    return alertType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  if (isLoading) {
    return <div className="alerts-loading">Loading alerts...</div>;
  }

  if (!alerts || alerts.length === 0) {
    return (
      <div className="alerts-empty">
        <div className="alerts-empty-icon">OK</div>
        <p>No active alerts</p>
        <p className="alerts-empty-subtitle">All monitored devices are operating normally.</p>
      </div>
    );
  }

  // Group alerts by severity
  const criticalAlerts = alerts.filter(a => a.severity?.toLowerCase() === 'critical');
  const warningAlerts = alerts.filter(a => a.severity?.toLowerCase() !== 'critical');

  return (
    <div className="alerts-active">
      {criticalAlerts.length > 0 && (
        <div className="alerts-group">
          <h4 className="alerts-group-header critical">Critical Alerts ({criticalAlerts.length})</h4>
          {criticalAlerts.map((alert, index) => (
            <AlertCard
              key={`critical-${index}`}
              alert={alert}
              getDeviceName={getDeviceName}
              formatAlertType={formatAlertType}
              formatTimestamp={formatTimestamp}
              formatDuration={formatDuration}
              getSeverityClass={getSeverityClass}
              getAlertIcon={getAlertIcon}
            />
          ))}
        </div>
      )}

      {warningAlerts.length > 0 && (
        <div className="alerts-group">
          <h4 className="alerts-group-header warning">Warning Alerts ({warningAlerts.length})</h4>
          {warningAlerts.map((alert, index) => (
            <AlertCard
              key={`warning-${index}`}
              alert={alert}
              getDeviceName={getDeviceName}
              formatAlertType={formatAlertType}
              formatTimestamp={formatTimestamp}
              formatDuration={formatDuration}
              getSeverityClass={getSeverityClass}
              getAlertIcon={getAlertIcon}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * AlertCard - Individual alert display component
 */
const AlertCard = ({
  alert,
  getDeviceName,
  formatAlertType,
  formatTimestamp,
  formatDuration,
  getSeverityClass,
  getAlertIcon
}) => {
  const deviceName = alert.device_name || getDeviceName(alert.device_id);

  return (
    <div className={`alert-card ${getSeverityClass(alert.severity)}`}>
      <div className="alert-card-header">
        <span className="alert-icon">{getAlertIcon(alert.alert_type)}</span>
        <span className="alert-device-name">{deviceName}</span>
        <span className="alert-type-badge">{formatAlertType(alert.alert_type)}</span>
      </div>

      <div className="alert-card-body">
        {alert.reason && (
          <p className="alert-reason">{alert.reason}</p>
        )}

        <div className="alert-details">
          <div className="alert-detail">
            <span className="detail-label">Detected:</span>
            <span className="detail-value">{formatTimestamp(alert.detected)}</span>
          </div>
          {alert.duration_minutes !== undefined && (
            <div className="alert-detail">
              <span className="detail-label">Duration:</span>
              <span className="detail-value">{formatDuration(alert.duration_minutes)}</span>
            </div>
          )}
          <div className="alert-detail">
            <span className="detail-label">Severity:</span>
            <span className={`detail-value severity-text ${getSeverityClass(alert.severity)}`}>
              {alert.severity || 'Warning'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertsActive;
